/**
 * TransliterationBar — Google Input Tools proxy UI. When the reporter
 * types Latin text and picks a language, this bar shows native-script
 * candidates for the last word. Click a candidate to insert into the
 * currently-focused editable (via `onPick(candidate)`).
 *
 * Kept intentionally minimal — the reporter can toggle it off if
 * they're writing in English.
 */
import { useEffect, useState } from "react";
import { Languages, Loader2 } from "lucide-react";
import { npApi } from "@/pages/newspulse/api";

const TRANSLIT_LANGS = [
  ["en", "English (off)"],
  ["hi", "हिन्दी"], ["bn", "বাংলা"], ["ta", "தமிழ்"], ["te", "తెలుగు"],
  ["ml", "മലയാളം"], ["kn", "ಕನ್ನಡ"], ["mr", "मराठी"], ["gu", "ગુજરાતી"],
  ["pa", "ਪੰਜਾਬੀ"], ["or", "ଓଡ଼ିଆ"], ["ur", "اردو"], ["ne", "नेपाली"],
  ["ar", "العربية"], ["fa", "فارسی"],
];

export default function TransliterationBar({ lang, onChangeLang, latestWord, onPick, className = "" }) {
  const [candidates, setCandidates] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!lang || lang === "en" || !latestWord || latestWord.length < 1) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    setBusy(true);
    npApi.transliterate(latestWord, lang, 5)
      .then((d) => { if (!cancelled) setCandidates(d.candidates || []); })
      .catch(() => { if (!cancelled) setCandidates([]); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [lang, latestWord]);

  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 ${className}`}
         data-testid="transliteration-bar">
      <Languages className="w-4 h-4 text-slate-500 shrink-0" />
      <select value={lang || "en"} onChange={(e) => onChangeLang && onChangeLang(e.target.value)}
              className="text-xs bg-white border border-slate-200 rounded-md px-2 py-1"
              data-testid="transliteration-bar-lang">
        {TRANSLIT_LANGS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
      </select>
      {lang && lang !== "en" && (
        <>
          <span className="text-[11px] text-slate-500 shrink-0">
            {latestWord ? `Suggestions for "${latestWord}"` : "Type a word for suggestions"}
          </span>
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          <div className="flex flex-wrap gap-1.5 ml-auto" data-testid="transliteration-bar-candidates">
            {candidates.map((c, i) => (
              <button key={`${c}-${i}`} type="button" onClick={() => onPick && onPick(c)}
                      className="text-sm bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-md px-2 py-1 transition-colors"
                      data-testid={`transliteration-cand-${i}`}>
                {c}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
